import { SaveOutlined } from "@ant-design/icons";
import { Button, Divider, Drawer, Flex, Form, Segmented, Select, message } from "antd";
import { useEffect, useState } from "react";
import { PRACTICE_AREA } from "../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { ehrOptions, referralSource } from "../../pages/onboarding/onboarding.constant";
import { getOnboardingCatalog, getUserInfo, patchUserInfo } from "../../service/auth.service";
import { setShowSettingsModal } from "../../slices/appStateSlice";
import { setUserInfo } from "../../slices/authSlice";
import { OnboardingData, OnboardingFormData } from "../../types/index.type";
import AutoShareManager from "../AutoShareManager/AutoShareManager.component";

const Settings = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileView();
  const { showSettingsModal, isMobilePath } = useAppSelector((state) => state.appState);
  const [practiceAreas, setPracticeAreas] = useState<{ label: string; value: string }[]>([]);
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<string | undefined>("behavioral_health");
  const [specialtiesByArea, setSpecialtiesByArea] = useState<Record<string, { label: string; value: string }[]>>({});
  const { userInfo } = useAppSelector((state) => state.auth);

  useEffect(() => {
    (async () => {
      try {
        if (showSettingsModal) {
          const data = await getOnboardingCatalog();
          const pa = (data.practice_areas || []).map((p) => ({ label: p.label, value: p.code }));
          const sba: Record<string, { label: string; value: string }[]> = {};
          Object.entries(data.specialties_by_area || {}).forEach(([area, list]) => {
            sba[area] = (list as any[]).map((s: any) => ({ label: s.label, value: s.code }));
          });
          setPracticeAreas(pa);
          setSpecialtiesByArea(sba);
          setFormValues();
        }
      } catch (e) {
        console.error("Failed to load onboarding catalogs", e);
      }
    })();
  }, [showSettingsModal]);

  const setFormValues = () => {
    form.setFieldsValue({
      practice_area: userInfo.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH ? "behavioral_health" : "medicine",
      specialty: userInfo.specialty_code || "",
      referral_source: userInfo.onboarding_data?.referral_source,
      ehr: userInfo.onboarding_data?.ehr,
      work_as: userInfo.onboarding_data?.work_as,
    });
    if (userInfo.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH) {
      setSelectedPracticeArea("behavioral_health");
    } else {
      setSelectedPracticeArea("medicine");
    }
  };

  const handleSave = async (values: OnboardingFormData) => {
    try {
      const onboardingData: OnboardingData = {
        specialty: values.specialty,
        referral_source: values.referral_source,
        ehr: values.ehr,
        work_as: values.work_as,
        completed: true,
        start_time: new Date().toISOString(),
      };

      await patchUserInfo({
        practice_area: values.practice_area,
        onboarding_data: onboardingData,
      });

      message.success("Profile updated successfully!");
      const response = await getUserInfo();
      const data = await response.data;
      dispatch(setUserInfo(data));
    } catch (error) {
      console.error("Error updating onboarding data:", error);
      message.error("Failed to update profile. Please try again.");
    }
  };

  return (
    <Drawer
      title="Settings"
      open={showSettingsModal}
      onClose={() => dispatch(setShowSettingsModal(false))}
      width={700}
      height={isMobile && !isMobilePath ? "80%" : "100%"}
      placement={isMobile ? "bottom" : "right"}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          work_as: "solo",
        }}
        onFinish={handleSave}
      >
        <Form.Item
          name="practice_area"
          label="My practice area is"
          rules={[{ required: true, message: "Please select your practice area" }]}
        >
          <Select disabled>
            {practiceAreas.map((opt: { label: string; value: string }) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="specialty"
          label="My specialty is"
          rules={[{ required: true, message: "Please select your specialty" }]}
        >
          <Select placeholder="Select your specialty" disabled={!selectedPracticeArea}>
            {(specialtiesByArea[selectedPracticeArea || "behavioral_health"] || []).map(
              (opt: { label: string; value: string }) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ),
            )}
          </Select>
        </Form.Item>

        {/* Flex instead of Row gutter — Row's negative side margins overflow the drawer body horizontally. */}
        <Flex gap={16} wrap>
          <Form.Item
            name="referral_source"
            label="How did you hear about us?"
            rules={[{ required: true, message: "Please select the referral source" }]}
            style={{ flex: "1 1 240px", minWidth: 0 }}
          >
            <Select placeholder="Select referral source">
              {referralSource.map((opt: { label: string; value: string }) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="ehr"
            label="What EHR do you use?"
            rules={[{ required: true, message: "Please select your EHR system" }]}
            style={{ flex: "1 1 240px", minWidth: 0 }}
          >
            <Select showSearch optionFilterProp="children" placeholder="Select your EHR system">
              {ehrOptions.map((opt: { label: string; value: string }) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Flex>

        <Form.Item
          name="work_as"
          label="How many providers would use this software in your practice?"
          rules={[{ required: true, message: "Please select any one option" }]}
        >
          <Segmented
            block
            options={[
              { label: "Solo", value: "solo" },
              { label: "2-10", value: "2-10" },
              { label: "11-50", value: "11-50" },
              { label: "50+", value: "50+" },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Button icon={<SaveOutlined />} style={{ width: "100%" }} type="primary" htmlType="submit">
            Save
          </Button>
        </Form.Item>
      </Form>

      {/* Patient auto-sharing — group users only (needs teammates to share with). */}
      {showSettingsModal && !!userInfo.group_name && (
        <>
          <Divider>Patient sharing</Divider>
          <AutoShareManager />
        </>
      )}
    </Drawer>
  );
};

export default Settings;
